import React from 'react';
import { useFetchPracticeLeads } from '../../hooks/Practice/useFetchPracticeLeads';

interface Lead {
  _id?: string;
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/** Backend field names seen for free-text client / intake notes on a lead */
const LEAD_NOTES_KEYS = [
  'comment',
  'comments',
  'notes',
  'message',
  'additionalNotes',
  'clientNotes',
  'client_comment',
  'clientComment',
  'details',
  'description',
  'concerns',
  'additional_info',
  'additionalInfo',
  'intakeNotes',
] as const;

function getLeadNotesDisplay(lead: Lead): string {
  for (const key of LEAD_NOTES_KEYS) {
    const v = lead[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (Array.isArray(v) && v.length) return v.map(String).filter(Boolean).join(', ');
  }
  return '';
}
interface ILeadsTable {
  idPractice: string;
}

export const LeadsTable: React.FC<ILeadsTable> = ({ idPractice }) => {
  const { leads, isLoading, error } = useFetchPracticeLeads(idPractice);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-gray-600">Loading leads...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 font-medium">Error loading leads</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white/60 backdrop-blur-xl rounded-1xl border border-white/20 shadow-lg p-2">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Leads List</h3>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] max-w-md">
              Client notes
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {!leads || leads.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                No leads found.
              </td>
            </tr>
          ) : (
            (leads as Lead[]).map((lead: Lead) => {
              const leadId = lead._id || lead.id || '';
              const notes = getLeadNotesDisplay(lead);
              return (
                <tr key={leadId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lead.name || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {lead.email || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {lead.phone || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      lead.status === 'contacted'
                        ? 'bg-green-100 text-green-800'
                        : lead.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {lead.status || 'pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md align-top">
                    {notes ? (
                      <p className="line-clamp-4 whitespace-pre-wrap break-words" title={notes}>
                        {notes}
                      </p>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {lead.createdAt
                      ? new Date(lead.createdAt).toLocaleDateString()
                      : '—'}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};
